import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';

const LipidGeneticTesting = () => {
  const [activeContentType, setActiveContentType] = useState<string | null>(null);

  const videoContent = [
    {
      title: 'Understanding Your Lipid Panel Results',
      duration: '10:35',
      views: '31K views',
      thumbnail:
        'https://public.readdy.ai/ai/img_res/caa3e8cc88c18243806acafa18b98a38.jpg',
    },
    {
      title: 'Genetic Testing for Heart Disease: What to Expect',
      duration: '14:20',
      views: '24K views',
      thumbnail:
        'https://readdy.ai/api/search-image?query=Genetic%20counselor%20explaining%20DNA%20test%20results%20for%20inherited%20heart%20disease%20risk%20to%20patient%20using%20chromosome%20diagram%20on%20tablet%20screen%20in%20modern%20genetics%20clinic%20warm%20natural%20lighting%20professional%20healthcare%20consultation%20photography&width=400&height=250&seq=lipid-test-vid-thumb2&orientation=landscape',
    },
    {
      title: 'Why Early Lipid Screening Saves Lives',
      duration: '8:45',
      views: '18K views',
      thumbnail:
        'https://readdy.ai/api/search-image?query=Medical%20professional%20drawing%20blood%20sample%20from%20patient%20arm%20for%20lipid%20profile%20cholesterol%20screening%20test%20in%20bright%20modern%20clinical%20laboratory%20with%20test%20tubes%20and%20centrifuge%20equipment%20warm%20professional%20healthcare%20photography&width=400&height=250&seq=lipid-test-vid-thumb3&orientation=landscape',
    },
  ];

  const handleContentTypeClick = (id: string) => {
    setActiveContentType(activeContentType === id ? null : id);
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Section */}
      <section className="pt-28 pb-16 bg-gradient-to-br from-[#fef9f9] via-[#fef5f5] to-[#faf5f8]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <span className="inline-flex items-center gap-2 px-4 py-2 bg-white rounded-full text-sm text-gray-600 mb-6 shadow-sm">
              <i className="ri-test-tube-line text-[#f04e58]"></i>
              Lipid &amp; Genetic Diagnostics
            </span>
            <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 leading-tight mb-6">
              Lipid &amp; Genetic Testing<br />for Heart Disease
            </h1>
            <p className="text-gray-600 text-lg leading-relaxed max-w-3xl mx-auto">
              Lipid panels and genetic testing are the cornerstone of identifying inherited cardiovascular risk. Understanding your cholesterol numbers and DNA can unlock life-saving insights—catching conditions like Familial Hypercholesterolemia before symptoms ever appear.
            </p>
          </div>

          {/* Content Type Cards */}
          <div className="grid grid-cols-3 gap-6">
            {/* Blog Card */}
            <div
              onClick={() => handleContentTypeClick('blog')}
              className={`relative bg-white rounded-2xl overflow-hidden cursor-pointer transition-all duration-300 hover:shadow-lg group ${
                activeContentType === 'blog'
                  ? 'ring-2 ring-[#f04e58] shadow-lg'
                  : 'shadow-sm hover:shadow-md'
              }`}
            >
              <div className="w-full h-48">
                <img
                  src="https://readdy.ai/api/search-image?query=Medical%20laboratory%20technician%20analyzing%20blood%20lipid%20panel%20test%20results%20on%20modern%20diagnostic%20equipment%20with%20cholesterol%20test%20tubes%20and%20genetic%20sequencing%20data%20on%20screen%20in%20bright%20clean%20clinical%20lab%20warm%20professional%20lighting%20healthcare%20diagnostics%20photography&width=400&height=300&seq=lipid-gen-blog-card-001&orientation=landscape"
                  alt="Blog articles about lipid and genetic testing"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="p-6">
                <div
                  className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 transition-colors ${
                    activeContentType === 'blog' ? 'bg-[#f04e58]' : 'bg-[#fef5f5] group-hover:bg-[#f04e58]'
                  }`}
                >
                  <i
                    className={`ri-article-line text-xl transition-colors ${
                      activeContentType === 'blog' ? 'text-white' : 'text-[#f04e58] group-hover:text-white'
                    }`}
                  ></i>
                </div>
                <h3 className="font-semibold text-gray-900 text-lg mb-2">Blog</h3>
                <p className="text-gray-500 text-sm">In-depth guide to lipid &amp; genetic testing</p>
                <div
                  className={`absolute top-4 right-4 w-8 h-8 rounded-full flex items-center justify-center transition-all ${
                    activeContentType === 'blog' ? 'bg-[#f04e58] rotate-45' : 'bg-white/90 backdrop-blur-sm'
                  }`}
                >
                  <i
                    className={`ri-add-line text-lg ${
                      activeContentType === 'blog' ? 'text-white' : 'text-gray-700'
                    }`}
                  ></i>
                </div>
              </div>
            </div>

            {/* Video Card */}
            <div
              onClick={() => handleContentTypeClick('videos')}
              className={`relative bg-white rounded-2xl overflow-hidden cursor-pointer transition-all duration-300 hover:shadow-lg group ${
                activeContentType === 'videos'
                  ? 'ring-2 ring-[#f04e58] shadow-lg'
                  : 'shadow-sm hover:shadow-md'
              }`}
            >
              <div className="w-full h-48">
                <img
                  src="https://readdy.ai/api/search-image?query=Cardiologist%20presenting%20lipid%20testing%20and%20genetic%20screening%20results%20to%20patient%20on%20digital%20display%20showing%20cholesterol%20levels%20DNA%20helix%20and%20cardiovascular%20risk%20assessment%20in%20modern%20cardiology%20office%20warm%20professional%20lighting%20healthcare%20education%20photography&width=400&height=300&seq=lipid-gen-video-card-001&orientation=landscape"
                  alt="Educational videos about lipid and genetic testing"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="p-6">
                <div
                  className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 transition-colors ${
                    activeContentType === 'videos' ? 'bg-[#f04e58]' : 'bg-[#fef5f5] group-hover:bg-[#f04e58]'
                  }`}
                >
                  <i
                    className={`ri-video-line text-xl transition-colors ${
                      activeContentType === 'videos' ? 'text-white' : 'text-[#f04e58] group-hover:text-white'
                    }`}
                  ></i>
                </div>
                <h3 className="font-semibold text-gray-900 text-lg mb-2">Video</h3>
                <p className="text-gray-500 text-sm">Visual explainers on lipid diagnostics</p>
                <div
                  className={`absolute top-4 right-4 w-8 h-8 rounded-full flex items-center justify-center transition-all ${
                    activeContentType === 'videos' ? 'bg-[#f04e58] rotate-45' : 'bg-white/90 backdrop-blur-sm'
                  }`}
                >
                  <i
                    className={`ri-add-line text-lg ${
                      activeContentType === 'videos' ? 'text-white' : 'text-gray-700'
                    }`}
                  ></i>
                </div>
              </div>
            </div>

            {/* Infograph Card */}
            <div
              onClick={() => handleContentTypeClick('infograph')}
              className={`relative bg-white rounded-2xl overflow-hidden cursor-pointer transition-all duration-300 hover:shadow-lg group ${
                activeContentType === 'infograph'
                  ? 'ring-2 ring-[#f04e58] shadow-lg'
                  : 'shadow-sm hover:shadow-md'
              }`}
            >
              <div className="w-full h-48">
                <img
                  src="https://readdy.ai/api/search-image?query=Colorful%20medical%20infographic%20poster%20showing%20lipid%20panel%20breakdown%20with%20LDL%20HDL%20triglycerides%20cholesterol%20levels%20and%20genetic%20testing%20DNA%20analysis%20for%20inherited%20heart%20disease%20risk%20modern%20healthcare%20data%20visualization%20coral%20teal%20cream%20color%20palette%20clean%20professional%20design&width=400&height=300&seq=lipid-gen-infograph-card-001&orientation=landscape"
                  alt="Lipid and genetic testing infographics"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="p-6">
                <div
                  className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 transition-colors ${
                    activeContentType === 'infograph' ? 'bg-[#f04e58]' : 'bg-[#fef5f5] group-hover:bg-[#f04e58]'
                  }`}
                >
                  <i
                    className={`ri-bar-chart-box-line text-xl transition-colors ${
                      activeContentType === 'infograph' ? 'text-white' : 'text-[#f04e58] group-hover:text-white'
                    }`}
                  ></i>
                </div>
                <h3 className="font-semibold text-gray-900 text-lg mb-2">Infograph</h3>
                <p className="text-gray-500 text-sm">Visual guide to testing pathways</p>
                <div
                  className={`absolute top-4 right-4 w-8 h-8 rounded-full flex items-center justify-center transition-all ${
                    activeContentType === 'infograph' ? 'bg-[#f04e58] rotate-45' : 'bg-white/90 backdrop-blur-sm'
                  }`}
                >
                  <i
                    className={`ri-add-line text-lg ${
                      activeContentType === 'infograph' ? 'text-white' : 'text-gray-700'
                    }`}
                  ></i>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Blog Content Section */}
      {activeContentType === 'blog' && (
        <section className="py-20 bg-white">
          <div className="max-w-4xl mx-auto px-6">
            {/* Article Header */}
            <div className="mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-6 leading-tight">
                Why Your "Normal" Cholesterol Might Be Lying to You: 5 Hidden Truths in Modern Lipid Testing
              </h2>
              <div className="flex items-center gap-4 text-sm text-gray-500 mb-8">
                <span className="flex items-center gap-2">
                  <i className="ri-calendar-line"></i>
                  Jan 18, 2025
                </span>
                <span className="flex items-center gap-2">
                  <i className="ri-time-line"></i>
                  12 min read
                </span>
              </div>
              <div className="w-full h-[400px] rounded-2xl overflow-hidden mb-8">
                <img
                  src="https://readdy.ai/api/search-image?query=Modern%20medical%20laboratory%20with%20advanced%20lipid%20testing%20equipment%20showing%20cholesterol%20particle%20analysis%20on%20digital%20screens%20scientist%20examining%20blood%20samples%20professional%20healthcare%20photography%20warm%20lighting%20clean%20aesthetic&width=800&height=400&seq=lgt-blog-hero-modern&orientation=landscape"
                  alt="Modern Lipid Testing"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>

            {/* Article Content */}
            <article className="prose prose-lg max-w-none">
              {/* Introduction */}
              <div className="mb-12">
                <p className="text-gray-700 leading-relaxed mb-4 text-lg">
                  For many patients, a routine check-up culminates in a sigh of relief when a physician remarks, "Your cholesterol looks normal." You glance at a standard lipid panel, see an LDL-C in the double digits, and assume your cardiovascular future is secure. However, in the realm of preventive cardiology, we have come to recognize this as a potentially dangerous false sense of security.
                </p>
                <p className="text-gray-700 leading-relaxed mb-4">
                  The reality is that traditional metrics often mask significant risk through a phenomenon known as <strong>lipid discordance</strong>. This occurs when your standard cholesterol weight (LDL-C) appears low or "normal," while your actual particle count (LDL-P or ApoB) remains dangerously high. If you are relying solely on legacy markers, you are viewing a low-resolution snapshot of a high-definition problem. To truly understand your risk, you must look beyond the "basic burden" and embrace the precise science of lipoproteins.
                </p>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Here are five hidden truths revealed by the new standard of care in advanced lipid testing.
                </p>
              </div>

              {/* Truth 1 */}
              <div className="mb-12 bg-[#fef9f9] rounded-2xl p-8">
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#f04e58] text-white font-bold text-xl flex-shrink-0">
                    1
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mt-1">
                    You Can Have Light Cargo but a Dangerous Number of Trucks (LDL-P)
                  </h3>
                </div>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Traditional testing measures LDL-C, which represents the total mass or weight of "bad" cholesterol within a volume of blood. To understand why this lies, consider the <strong>"Truck Analogy."</strong> If LDL-C is the total weight of the cargo being transported through your "arterial highway," LDL-P (LDL Particle Number) is the actual number of trucks on the road.
                </p>
                <div className="bg-white rounded-xl p-6 mb-4">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="p-4 bg-[#fef5f5] rounded-lg border-l-4 border-[#f04e58]">
                      <div className="flex items-center gap-3 mb-2">
                        <i className="ri-truck-line text-[#f04e58] text-2xl"></i>
                        <h4 className="font-semibold text-gray-900">LDL-C</h4>
                      </div>
                      <p className="text-sm text-gray-600 leading-relaxed">
                        Total weight of cholesterol cargo
                      </p>
                    </div>
                    <div className="p-4 bg-[#f0fdf4] rounded-lg border-l-4 border-[#0d9488]">
                      <div className="flex items-center gap-3 mb-2">
                        <i className="ri-roadster-line text-[#0d9488] text-2xl"></i>
                        <h4 className="font-semibold text-gray-900">LDL-P</h4>
                      </div>
                      <p className="text-sm text-gray-600 leading-relaxed">
                        Actual number of trucks on the road
                      </p>
                    </div>
                  </div>
                </div>
                <p className="text-gray-700 leading-relaxed mb-4">
                  The hidden truth is that it is the number of trucks (accidents), not the weight of the cargo, that causes the traffic jam (plaque formation). A patient can have a "normal" LDL-C because their cargo is light, yet still possess a high LDL-P count. This discordance is a major driver of "unexplained" heart attacks.
                </p>
                <div className="bg-gradient-to-r from-[#f04e58] to-[#0d9488] rounded-xl p-6 text-white mb-4">
                  <p className="text-xl font-bold text-white text-center leading-relaxed">
                    "High LDL-P increases plaque risk even if LDL-C is controlled."
                  </p>
                </div>
                <p className="text-gray-700 leading-relaxed">
                  Furthermore, for those with elevated triglycerides, the standard panel's "Non-HDL" calculation (Total Cholesterol minus HDL) actually serves as a far better predictor of risk than LDL-C alone, as it begins to account for the total burden of all harmful particles.
                </p>
              </div>

              {/* Truth 2 */}
              <div className="mb-12 bg-[#fef9f9] rounded-2xl p-8">
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#f04e58] text-white font-bold text-xl flex-shrink-0">
                    2
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mt-1">
                    ApoB is the Modern Gold Standard for Total Atherogenic Burden
                  </h3>
                </div>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Perhaps the most frustrating "hidden truth" in cardiology is <strong>Lipoprotein(a), or Lp(a)</strong>. This is a genetically inherited, LDL-like particle that possesses distinct pro-thrombotic (clotting) properties.
                </p>
                <div className="bg-white rounded-xl p-6 mb-4">
                  <div className="flex items-center justify-center mb-4">
                    <div className="text-center">
                      <div className="w-32 h-32 flex items-center justify-center rounded-full bg-[#0d9488]/10 mx-auto mb-3">
                        <i className="ri-focus-3-line text-5xl text-[#0d9488]"></i>
                      </div>
                      <p className="text-sm text-gray-600 font-medium">One ApoB Protein Per Particle</p>
                    </div>
                  </div>
                </div>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Every single potentially plaque-forming particle—whether it is VLDL, IDL, or LDL—carries exactly <strong>one ApoB protein</strong> on its surface. Therefore, measuring ApoB provides a literal, direct count of the total number of atherogenic particles in circulation.
                </p>
                <div className="bg-white rounded-xl p-6 border-l-4 border-[#0d9488]">
                  <p className="text-gray-900 font-semibold mb-2">Key Insight:</p>
                  <p className="text-gray-700 leading-relaxed">
                    Unlike LDL-C, which only measures the cholesterol inside the particles, ApoB captures the total burden of every particle capable of invading the arterial wall. Shifting the clinical focus to ApoB provides a crystalline view of a patient's risk that a standard panel simply cannot replicate.
                  </p>
                </div>
              </div>

              {/* Truth 3 */}
              <div className="mb-12 bg-[#fef9f9] rounded-2xl p-8">
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#f04e58] text-white font-bold text-xl flex-shrink-0">
                    3
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mt-1">
                    The Lp(a) Paradox—The Risk You Cannot Out-Run
                  </h3>
                </div>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Lp(a) is a genetically inherited, LDL-like particle that possesses distinct pro-thrombotic (clotting) properties.
                </p>
                <div className="bg-white rounded-xl p-6 mb-4">
                  <h4 className="font-semibold text-gray-900 mb-3">Lp(a) is a Dual Threat:</h4>
                  <div className="space-y-3">
                    <div className="flex items-start gap-3">
                      <i className="ri-alert-line text-[#f04e58] text-xl mt-0.5 flex-shrink-0"></i>
                      <p className="text-gray-700 leading-relaxed">
                        It accelerates the development of arterial plaque
                      </p>
                    </div>
                    <div className="flex items-start gap-3">
                      <i className="ri-alert-line text-[#f04e58] text-xl mt-0.5 flex-shrink-0"></i>
                      <p className="text-gray-700 leading-relaxed">
                        It increases the risk of life-threatening clot formation (thrombosis)
                      </p>
                    </div>
                  </div>
                </div>
                <p className="text-gray-700 leading-relaxed mb-4">
                  The paradox of Lp(a) lies in its resistance to traditional intervention; unlike standard LDL, Lp(a) levels are almost entirely determined by your DNA and cannot be significantly altered by diet or exercise.
                </p>
                <div className="bg-gradient-to-r from-[#f04e58] to-[#d93d47] rounded-xl p-6 text-white">
                  <p className="font-semibold mb-2 text-white">The Heartbreaking Scenario:</p>
                  <p className="text-white/95 leading-relaxed">
                    A patient can do "everything right"—run marathons and eat a pristine diet—and yet remain at high risk for premature heart disease. Identifying this silent genetic factor is essential, especially for those with a family history of early cardiac events.
                  </p>
                </div>
              </div>

              {/* Truth 4 */}
              <div className="mb-12 bg-[#fef9f9] rounded-2xl p-8">
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#f04e58] text-white font-bold text-xl flex-shrink-0">
                    4
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mt-1">
                    Morphological Profiles—Why Small and Dense is Deadly
                  </h3>
                </div>
                <p className="text-gray-700 leading-relaxed mb-4">
                  In lipidology, the morphological profile of the lipoproteins dictates their pathogenicity. Advanced testing such as <strong>NMR Lipoprotein Analysis</strong> separates LDL into two main categories: "large, buoyant" and "small, dense."
                </p>
                <div className="grid md:grid-cols-2 gap-4 mb-4">
                  <div className="bg-white rounded-xl p-5 border-l-4 border-[#10b981]">
                    <div className="flex items-center gap-3 mb-2">
                      <i className="ri-checkbox-circle-line text-[#10b981] text-2xl"></i>
                      <h4 className="font-semibold text-gray-900">Large, Buoyant LDL</h4>
                    </div>
                    <p className="text-gray-600 text-sm leading-relaxed">
                      Less dangerous, harder to penetrate arterial walls
                    </p>
                  </div>
                  <div className="bg-white rounded-xl p-5 border-l-4 border-[#f04e58]">
                    <div className="flex items-center gap-3 mb-2">
                      <i className="ri-close-circle-line text-[#f04e58] text-2xl"></i>
                      <h4 className="font-semibold text-gray-900">Small, Dense LDL</h4>
                    </div>
                    <p className="text-gray-600 text-sm leading-relaxed">
                      Highly dangerous, easily penetrates arterial walls
                    </p>
                  </div>
                </div>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Small, dense LDL particles are significantly more dangerous because their diminished size allows them to penetrate the endothelial lining of the artery walls with far greater ease. This subfraction is highly atherogenic and is frequently a red flag for underlying insulin resistance.
                </p>
                <div className="bg-white rounded-xl p-6 border-l-4 border-[#f04e58]">
                  <p className="text-gray-900 font-semibold mb-2">Critical Interconnectivity:</p>
                  <p className="text-gray-700 leading-relaxed">
                    Patients with a high concentration of small, dense LDL often have a much higher total particle count (Takeaway 1), even if their total cholesterol weight appears unremarkable. Size doesn't just matter—it defines the level of aggression with which cholesterol attacks your arteries.
                  </p>
                </div>
              </div>

              {/* Truth 5 */}
              <div className="mb-12 bg-[#fef9f9] rounded-2xl p-8">
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#f04e58] text-white font-bold text-xl flex-shrink-0">
                    5
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mt-1">
                    Precision Genetics as a Roadmap for Family Legacies
                  </h3>
                </div>
                <p className="text-gray-700 leading-relaxed mb-4">
                  For individuals with severe cholesterol elevations, genetic testing is no longer optional; it is a critical diagnostic roadmap. This is particularly vital for identifying:
                </p>
                <div className="bg-white rounded-xl p-6 mb-4">
                  <div className="space-y-3">
                    <div className="flex items-start gap-3">
                      <div className="w-2 h-2 rounded-full bg-[#f04e58] mt-2 flex-shrink-0"></div>
                      <div>
                        <p className="text-gray-900 font-semibold mb-1">HeFH (Heterozygous Familial Hypercholesterolemia)</p>
                        <p className="text-gray-600 text-sm leading-relaxed">
                          Where LDL-C is typically &gt;190 mg/dL.
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-2 h-2 rounded-full bg-[#f04e58] mt-2 flex-shrink-0"></div>
                      <div>
                        <p className="text-gray-900 font-semibold mb-1">HoFH (Homozygous Familial Hypercholesterolemia)</p>
                        <p className="text-gray-600 text-sm leading-relaxed">
                          A rare, severe condition where untreated LDL-C can reach &gt;400–500 mg/dL.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Testing focuses on mutations in the <strong>LDLR</strong> (receptor defects), <strong>APOB</strong> (binding impairments), and <strong>PCSK9</strong> (receptor breakdown) genes. Confirming these mutations does more than validate a diagnosis—it enables life-saving family cascade screening.
                </p>
                <div className="bg-gradient-to-r from-[#0d9488] to-[#10b981] rounded-xl p-6 text-white">
                  <p className="text-white/90 leading-relaxed">
                    When a genetic driver is identified in one individual, we can proactively screen and protect their entire family tree, transforming a reactive treatment plan into a <strong className="text-white">proactive legacy of prevention</strong>.
                  </p>
                </div>
              </div>

              {/* The Safety Check */}
              <div className="mb-12 bg-[#fef9f9] rounded-2xl p-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                  The Safety Check: Monitoring the System
                </h3>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Advanced lipid management requires a holistic view of how the body responds to both metabolic stress and therapy. A comprehensive monitoring strategy includes:
                </p>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="bg-white rounded-xl p-5 border-l-4 border-[#f04e58]">
                    <div className="flex items-center gap-3 mb-2">
                      <i className="ri-pulse-line text-[#f04e58] text-xl"></i>
                      <h4 className="font-semibold text-gray-900">HbA1c</h4>
                    </div>
                    <p className="text-gray-600 text-sm leading-relaxed">
                      To assess long-term blood sugar levels, as insulin resistance often drives the production of small, dense LDL.
                    </p>
                  </div>
                  <div className="bg-white rounded-xl p-5 border-l-4 border-[#0d9488]">
                    <div className="flex items-center gap-3 mb-2">
                      <i className="ri-heart-pulse-line text-[#0d9488] text-xl"></i>
                      <h4 className="font-semibold text-gray-900">Liver Function (ALT, AST)</h4>
                    </div>
                    <p className="text-gray-600 text-sm leading-relaxed">
                      To ensure the safety and tolerability of lipid-lowering medications.
                    </p>
                  </div>
                  <div className="bg-white rounded-xl p-5 border-l-4 border-[#f04e58]">
                    <div className="flex items-center gap-3 mb-2">
                      <i className="ri-flashlight-line text-[#f04e58] text-xl"></i>
                      <h4 className="font-semibold text-gray-900">Creatine Kinase (CK)</h4>
                    </div>
                    <p className="text-gray-600 text-sm leading-relaxed">
                      To monitor for potential muscle injury associated with certain therapies.
                    </p>
                  </div>
                  <div className="bg-white rounded-xl p-5 border-l-4 border-[#0d9488]">
                    <div className="flex items-center gap-3 mb-2">
                      <i className="ri-test-tube-line text-[#0d9488] text-xl"></i>
                      <h4 className="font-semibold text-gray-900">TSH (Thyroid Stimulating Hormone)</h4>
                    </div>
                    <p className="text-gray-600 text-sm leading-relaxed">
                      To rule out hypothyroidism, a frequent secondary cause of high cholesterol.
                    </p>
                  </div>
                </div>
              </div>

              {/* Conclusion */}
              <div className="mb-12 bg-gradient-to-br from-[#fef9f9] to-[#faf5f8] rounded-2xl p-8 border-2 border-[#0d9488]/20">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                  Beyond the Numbers
                </h3>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Modern lipid testing reveals a far more complex picture than traditional cholesterol panels. By understanding LDL-P, ApoB, Lp(a), and particle morphology, you gain a high-definition view of your true cardiovascular risk.
                </p>
                <p className="text-gray-700 leading-relaxed mb-4">
                  If you have a family history of heart disease or "normal" numbers that seem discordant with your health goals, it is time to demand a deeper look.
                </p>
                <p className="text-gray-900 font-semibold text-lg">
                  When was the last time you looked beyond the weight of your cholesterol to see the true number, size, and genetic signature of the particles within your own arteries?
                </p>
              </div>

              {/* Call to Action */}
              <div className="mt-12 bg-gradient-to-r from-[#f04e58] to-[#0d9488] rounded-2xl p-8 text-white">
                <h3 className="text-2xl font-bold mb-4">Get the Complete Picture</h3>
                <p className="text-white/90 leading-relaxed mb-6">
                  Advanced lipid testing can reveal hidden risks that standard panels miss. Our specialists can help you understand your true cardiovascular risk and create a personalized prevention plan.
                </p>
                <Link
                  to="/consultation"
                  className="inline-flex items-center px-6 py-3 bg-white text-[#f04e58] rounded-full font-semibold hover:bg-gray-100 transition-all cursor-pointer whitespace-nowrap"
                >
                  Schedule Advanced Testing
                  <i className="ri-arrow-right-line ml-2"></i>
                </Link>
              </div>
            </article>
          </div>
        </section>
      )}

      {/* Video Content Section */}
      {activeContentType === 'videos' && (
        <section className="py-20 bg-white">
          <div className="max-w-4xl mx-auto px-6">
            {/* Video Header */}
            <div className="mb-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Lipid &amp; Genetic Testing Explained
              </h2>
              <p className="text-gray-600 leading-relaxed">
                Watch this educational video to understand how lipid panels and genetic testing work together to identify inherited cardiovascular risk, and why early screening can be life-saving for you and your family.
              </p>
            </div>

            {/* Video Player */}
            <div className="relative w-full rounded-2xl overflow-hidden shadow-xl bg-gray-900 aspect-video mb-8">
              <video
                className="w-full h-full"
                controls
                autoPlay
                controlsList="nodownload"
                playsInline
                poster="https://readdy.ai/api/search-image?query=Cardiologist%20presenting%20lipid%20testing%20and%20genetic%20screening%20results%20to%20patient%20on%20digital%20display%20showing%20cholesterol%20levels%20DNA%20helix%20and%20cardiovascular%20risk%20assessment%20in%20modern%20cardiology%20office%20warm%20professional%20lighting%20healthcare%20education%20photography&width=800&height=450&seq=lipid-vid-poster-001&orientation=landscape"
              >
                <source
                  src="https://rr2---sn-nx57ynsk.googlevideo.com/videoplayback?expire=1771411630&ei=jnyVacuXFJPen88P4sjksQI&ip=151.186.183.87&id=f2ca3bf731f86f3f&itag=22&source=contrib_service_notebooklm&begin=0&requiressl=yes&xpc=EghoqJzIP3oBAQ==&met=1771404430,&mh=og&mm=32&mn=sn-nx57ynsk&ms=su&mv=m&mvi=2&pl=26&rms=su,su&sc=yes&susc=nblm&app=fife&ic=1045&eaua=vlflJW24Q14&pcm2=yes&mime=video/mp4&vprv=1&rqh=1&dur=463.191&lmt=1771400458908445&mt=1771404068&txp=0011224&sparams=expire,ei,ip,id,itag,source,requiressl,xpc,susc,app,ic,eaua,pcm2,mime,vprv,rqh,dur,lmt&sig=AJEij0EwRgIhAL-vmiQ0wXJwgZfsDPYMcZp0cT_rrXd73WwbavJ7XTfMAiEApbbsABujC8kCNIdEn1xcNA2DV9irlCwaDpoqyyUS1BQ=&lsparams=met,mh,mm,mn,ms,mv,mvi,pl,rms,sc&lsig=APaTxxMwRQIhAKg6o0d2ui-pwvX8q5nDO9K93Ms36Owue7hRjCfMgA4-AiBNnCRzc466NZfjlVqxvpyXtbPHeOEaacgPG12eIEDT7w=="
                  type="video/mp4"
                />
                Your browser does not support the video tag.
              </video>
            </div>

            {/* Video Info */}
            <div className="bg-[#fef9f9] rounded-2xl p-6">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-12 h-12 rounded-full bg-[#f04e58] flex items-center justify-center">
                  <i className="ri-play-fill text-white text-xl"></i>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">Lipid &amp; Genetic Testing Series</h3>
                  <p className="text-sm text-gray-500">Educational Content</p>
                </div>
              </div>
              <p className="text-gray-700 leading-relaxed">
                This series covers the complete diagnostic landscape for inherited cardiovascular disease—from understanding your lipid panel results and what each marker means, to the role of genetic testing in confirming Familial Hypercholesterolemia. Learn how cascade screening can protect your entire family and why early detection is the most powerful tool in preventive cardiology.
              </p>
            </div>

            {/* Related Videos */}
            <div className="mt-12">
              <h3 className="text-xl font-semibold text-gray-900 mb-6">Related Educational Videos</h3>
              <div className="grid md:grid-cols-3 gap-6">
                {videoContent.map((video, index) => (
                  <div
                    key={index}
                    className="bg-white rounded-xl overflow-hidden shadow-md hover:shadow-lg transition-shadow cursor-pointer group"
                  >
                    <div className="relative h-40 overflow-hidden">
                      <img
                        src={video.thumbnail}
                        alt={video.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                      />
                      <div className="absolute inset-0 bg-black/30 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                        <div className="w-12 h-12 rounded-full bg-white/90 flex items-center justify-center">
                          <i className="ri-play-fill text-[#f04e58] text-2xl"></i>
                        </div>
                      </div>
                      <div className="absolute bottom-2 right-2 bg-black/70 text-white text-xs px-2 py-1 rounded">
                        {video.duration}
                      </div>
                    </div>
                    <div className="p-4">
                      <h4 className="font-medium text-gray-900 text-sm mb-1 group-hover:text-[#f04e58] transition-colors">
                        {video.title}
                      </h4>
                      <p className="text-xs text-gray-500">{video.views}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Infograph Content Section */}
      {activeContentType === 'infograph' && (
        <section className="py-20 bg-white">
          <div className="max-w-5xl mx-auto px-6">
            {/* Infograph Header */}
            <div className="mb-8 text-center">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Lipid &amp; Genetic Testing: A Visual Guide
              </h2>
              <p className="text-gray-600 leading-relaxed max-w-3xl mx-auto">
                A comprehensive visual overview of lipid panel markers, genetic testing pathways, and how these diagnostics work together to identify and manage inherited cardiovascular risk.
              </p>
            </div>

            {/* Main Infographic Placeholder */}
            <div className="bg-[#fef9f9] rounded-2xl p-6 shadow-lg mb-12">
              <div className="w-full rounded-xl overflow-hidden">
                <img
                  src="https://static.readdy.ai/image/ff0b7e80ce034da2f0ffbed7fce4b50d/ec276b3a3ca6c0be2977008a7bad8ae3.png"
                  alt="Lipid Diagnostics: From Routine Screening to Advanced FH Detection - showing standard lipid panel, advanced particle testing, independent risk markers, genetic mutation confirmation, clinical LDL thresholds, and safety treatment monitoring"
                  className="w-full h-auto object-contain object-center"
                />
              </div>
            </div>

            {/* Key Stats */}
            <div className="grid grid-cols-4 gap-4 mb-12">
              <div className="bg-white rounded-xl p-5 border border-gray-100 shadow-sm text-center">
                <div className="text-3xl font-bold text-[#f04e58] mb-2">1 in 250</div>
                <p className="text-xs text-gray-600">People carry an FH mutation</p>
              </div>
              <div className="bg-white rounded-xl p-5 border border-gray-100 shadow-sm text-center">
                <div className="text-3xl font-bold text-[#0d9488] mb-2">90%</div>
                <p className="text-xs text-gray-600">Of FH cases remain undiagnosed</p>
              </div>
              <div className="bg-white rounded-xl p-5 border border-gray-100 shadow-sm text-center">
                <div className="text-3xl font-bold text-[#f04e58] mb-2">50%</div>
                <p className="text-xs text-gray-600">Risk passed to each child</p>
              </div>
              <div className="bg-white rounded-xl p-5 border border-gray-100 shadow-sm text-center">
                <div className="text-3xl font-bold text-[#0d9488] mb-2">76%</div>
                <p className="text-xs text-gray-600">Event reduction with early treatment</p>
              </div>
            </div>

            {/* Key Highlights */}
            <div className="grid md:grid-cols-3 gap-6">
              <div className="bg-white rounded-xl p-6 border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#f04e58]/10 mb-4">
                  <i className="ri-test-tube-line text-xl text-[#f04e58]"></i>
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">Lipid Panel</h3>
                <p className="text-sm text-gray-600 leading-relaxed">
                  A simple blood test measuring Total Cholesterol, LDL, HDL, and Triglycerides—the essential first step in assessing cardiovascular risk and guiding treatment decisions.
                </p>
              </div>

              <div className="bg-white rounded-xl p-6 border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#0d9488]/10 mb-4">
                  <i className="ri-dna-line text-xl text-[#0d9488]"></i>
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">Genetic Screening</h3>
                <p className="text-sm text-gray-600 leading-relaxed">
                  DNA analysis targeting LDLR, APOB, and PCSK9 genes to confirm inherited cholesterol disorders and distinguish genetic FH from lifestyle-related high cholesterol.
                </p>
              </div>

              <div className="bg-white rounded-xl p-6 border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#f04e58]/10 mb-4">
                  <i className="ri-group-line text-xl text-[#f04e58]"></i>
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">Cascade Screening</h3>
                <p className="text-sm text-gray-600 leading-relaxed">
                  Systematic testing of family members after one case is confirmed—the most cost-effective strategy for detecting FH and protecting entire families from silent risk.
                </p>
              </div>
            </div>

            {/* Share Options */}
            <div className="mt-10 flex flex-wrap justify-center gap-4">
              <button className="inline-flex items-center px-6 py-3 bg-[#f04e58] text-white rounded-full font-medium hover:bg-[#d93d47] transition-all cursor-pointer whitespace-nowrap">
                <i className="ri-download-line mr-2"></i>
                Download Infographic
              </button>
              <button className="inline-flex items-center px-6 py-3 border border-gray-300 text-gray-700 rounded-full font-medium hover:bg-gray-50 transition-all cursor-pointer whitespace-nowrap">
                <i className="ri-share-line mr-2"></i>
                Share
              </button>
            </div>
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
};

export default LipidGeneticTesting;
